import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'dpl/common/design_system/Button';
import VennImages from 'dpl/components/VennImages';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { FOREVER_DURATION, useSnoozed } from 'dpl/shared/hooks/useSnoozed';
import {
  RESULTS_TYPES,
  SEARCH_ALERTS_FEATURE_KEY
} from 'dpl/puppy_search_results/utils/constants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import useSearchFiltering from 'dpl/search_result_filters/hooks/useSearchFiltering';
import { setSearchAlertsDismissed } from 'dpl/actions/server';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { nearbyBreederProfilesResourceDefinition } from 'dpl/puppy_search_results/utils/queryDefinitions';
import {
  FILTER_LOCATION_NAME,
  FILTER_POSTAL_CODE
} from 'dpl/constants/query_params';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import useSearchAlertsMapBanner from 'dpl/puppy_search_results/hooks/useSearchAlertsMapBanner';
import useSearchLocation from 'dpl/search_result_filters/hooks/useSearchLocation';

import avatar1 from 'dpl/assets/search_alerts/avatar-1.png';
import avatar2 from 'dpl/assets/search_alerts/avatar-2.png';
import avatar3 from 'dpl/assets/search_alerts/avatar-3.png';

import { NearbyBreederProfilesResource } from '../../constants/resource_types';
import AlertsMobileMenu from './AlertsMobileMenu';
import AlertsModal from './AlertsModal';
import SearchAlertsMapBanner from './SearchAlertsMapBanner';
import { ALERTS_MODAL } from './SearchAlertsToggle';

const VENN_IMAGES = [
  {
    src: avatar1
  },
  {
    src: avatar2
  },
  {
    src: avatar3
  }
];

const IS_LT_LG = isLessThanBreakpoint('lg');

interface SearchAlertsBannerProps {
  /** Where the map banner can show: the mobile breeders feed. */
  isMapBannerEligible?: boolean;
}

export default function SearchAlertsBanner({
  isMapBannerEligible = false
}: SearchAlertsBannerProps) {
  const { isSnoozed, setSnoozed } = useSnoozed();
  const dispatch = useDispatch();
  const [showSuccessContent, setShowSuccessContent] = useState(false);
  const { resultsType } = useSearchFiltering();

  const { emailListJoinedAt, breedId, breedName } = useSelector(
    ({ server }: any) => server
  );
  const params = useSelector(({ queryParams }: any) => queryParams);

  const [openAlertsModal, setOpenAlertsModal] = useState<string | null>(null);

  const isBannerHidden =
    !showSuccessContent &&
    (emailListJoinedAt ||
      isSnoozed(`${SEARCH_ALERTS_FEATURE_KEY}-${breedId}`, FOREVER_DURATION));

  // The map is centered on, and labeled with, the search location.
  const { locationName } = useSearchLocation();
  const mapBanner = useSearchAlertsMapBanner(
    isMapBannerEligible && !isBannerHidden && Boolean(locationName)
  );

  const {
    isFetching,
    value: { data: nearbyBreeders }
  } = useResourceQuery<NearbyBreederProfilesResource>({
    ...nearbyBreederProfilesResourceDefinition(breedId, {
      location: params[FILTER_LOCATION_NAME],
      postal_code: params[FILTER_POSTAL_CODE],
      distance: 500,
      limit: 101
    }),
    enabled: Boolean(breedId) && !mapBanner.isShown
  });

  function getNearbyBreedersCountCopy() {
    if (nearbyBreeders.length === 101) {
      return '100+ vetted breeders';
    } else if (nearbyBreeders.length < 3) {
      return 'Vetted breeders';
    }
    return `${nearbyBreeders.length} vetted breeders`;
  }

  const analyticsView =
    resultsType === RESULTS_TYPES.PUPPIES
      ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
      : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;

  function handleButtonClick() {
    setOpenAlertsModal(ALERTS_MODAL.SIGN_UP);
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: 'get_alerts',
      view: analyticsView,
      view_feature: ANALYTICS_VIEW_FEATURES.SEARCH_ALERTS_MODULE,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
    });
  }

  function onSuccess() {
    setShowSuccessContent(true);
    dispatch(setSearchAlertsDismissed(true));
  }

  const headerCopy = showSuccessContent
    ? 'You\u2019re signed up'
    : `Get alerts for new ${breedName} puppies near you`;

  const subtitleCopy = showSuccessContent
    ? 'We\u2019ll let you know as soon as breeders near you add new puppies and litters.'
    : 'Be the first to hear when puppies from our trusted network are available nearby.';

  return (
    <>
      {!isBannerHidden && mapBanner.isShown && (
        <SearchAlertsMapBanner
          banner={mapBanner}
          locationName={mapBanner.locationName ?? (locationName as string)}
          showSuccessContent={showSuccessContent}
          successSubtitle={subtitleCopy}
          onGetAlertsClick={handleButtonClick}
        />
      )}
      {!isBannerHidden && !mapBanner.isShown && (
        <div className="SearchAlertsBanner bg-sand pv10 pv14-lg br4-l nh5 ph5 nh0-lg ph0-lg">
          <div className="flex justify-center-l mb6 items-center">
            <VennImages
              images={VENN_IMAGES}
              width="36px"
              height="36px"
              isOverlapping
              isBordered
              isSmall
              className="mr2"
            />
            {isFetching ? (
              <div>
                <SkeletonBlock
                  className="font-14 md:font-16 mb1"
                  height="1em"
                  width="10em"
                  maxWidth="75%"
                />
                <SkeletonBlock
                  className="font-14 md:font-16"
                  height="1em"
                  width="4em"
                  maxWidth="50%"
                />
              </div>
            ) : (
              <p className="secondary font-14 lh-normal">
                {getNearbyBreedersCountCopy()}
                <br />
                in your area
              </p>
            )}
          </div>
          <h4 className="font-24 lg:font-32 tc-l mb4 mb2-lg lh-title">
            {headerCopy}
          </h4>
          <p className="secondary tc-l mb6">{subtitleCopy}</p>
          <div className="flex justify-center-l">
            {showSuccessContent ? (
              <div className="tertiary font-16 fw-medium pv3 ph4 ba b--default br2">
                Alerts enabled
              </div>
            ) : (
              <Button
                size="xxsmall"
                className="font-16"
                type="button"
                iconName="fetch-bell"
                iconPosition="left"
                iconSize="24px"
                onClick={handleButtonClick}
              >
                Get alerts
              </Button>
            )}
          </div>
        </div>
      )}
      {IS_LT_LG && (
        <AlertsMobileMenu
          openAlertsModal={openAlertsModal}
          setOpenAlertsModal={(value: any) => setOpenAlertsModal(value)}
          analyticsView={analyticsView}
          setSnoozed={setSnoozed}
          onSuccess={onSuccess}
        />
      )}
      {!IS_LT_LG && Boolean(openAlertsModal) && (
        <AlertsModal
          openAlertsModal={openAlertsModal}
          setOpenAlertsModal={(value: any) => setOpenAlertsModal(value)}
          analyticsView={analyticsView}
          setSnoozed={setSnoozed}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
}
